import { useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useIsAgencyCompany } from '@mint-lib/profile-helpers';
import { Auth } from '@mint-sdk/api-auth';

import { useAgencyClients } from '../../api/queries/agencyClients.js';
import { useUsers } from '../../api/queries/usersData.js';
import { buildUserFormData, UserType } from '../../api/users/index.js';
import { type UserTableV2Ref } from '../../features/UserTableV2/index.js';
import { isEqual } from '../../organisms/UserModal/helpers.js';
import { HandleSubmitPramsType } from '../../organisms/UserModal/userModal.types.js';
import { type UserInviteOperation } from './useUserInviteOperation.js';

type UseUsersParams = NonNullable<Parameters<typeof useUsers>[0]>;

type UseUserPageActionsParams = {
  tags: UseUsersParams['tags'];
  isAgGridEnabled: boolean;
  setIsNewModalOpened: (open: boolean) => void;
};

const useUserPageActions = ({
  tags,
  isAgGridEnabled,
  setIsNewModalOpened,
}: UseUserPageActionsParams) => {
  const { t } = useTranslation('@myn/permissions');
  const isAgencyCompany = useIsAgencyCompany();
  const { assignClients } = useAgencyClients();

  const { users, createUser, editUser, refetchUsers } = useUsers({
    tags,
    disableUsersFetch: isAgGridEnabled,
  });

  const createUserV2 = Auth.createUserV2();
  const editUserV2 = Auth.editUserV2();

  const [isUserExistInWorkspace, setIsUserExistInWorkspace] =
    useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<UserType | null>(null);
  const userTableV2Ref = useRef<UserTableV2Ref>(null);

  const refreshUsers = async () => {
    if (isAgGridEnabled) {
      await userTableV2Ref.current?.refresh();
    } else {
      refetchUsers();
    }
  };

  const handleCreate = async (
    params: HandleSubmitPramsType,
    inviteOperation: UserInviteOperation,
  ) => {
    inviteOperation.restartIfEnded();

    try {
      const data = isAgGridEnabled
        ? await createUserV2.mutateAsync({
            data: buildUserFormData(params.userData),
          })
        : await createUser.mutateAsync(params.userData);

      enqueueSnackbar({
        removable: true,
        variant: 'success',
        title: t('Success'),
        textLayout: 'multiline',
        subTitle: data.addInIdp
          ? t(
              // eslint-disable-next-line @mint/no-special-characters
              'This user’s organisation uses an IdP. Don’t forget to add them to the IdP so they can log in.',
            )
          : t('The user has been added successfully'),
      });

      // if passed clients and user created successfully
      // pass id from data and call assign
      if (
        isAgencyCompany &&
        params?.clients &&
        params?.clients?.length !== 0 &&
        data.id
      ) {
        try {
          await assignClients.mutateAsync({
            clients: params.clients,
            user: data.id,
          });
        } catch {
          enqueueSnackbar({
            removable: true,
            variant: 'error',
            title: t('Error'),
            textLayout: 'multiline',
            subTitle: t('Failed to assign clients to the user.'),
          });
          inviteOperation.fail('assign-clients');

          return null;
        }
      }

      inviteOperation.succeed();
      setIsNewModalOpened(false);

      await refreshUsers();
    } catch (error) {
      const status = error?.response?.status ?? error?.status;
      const emailError =
        error?.response?.data?.email?.[0] ?? error?.data?.email?.[0];
      if (status === 400 && emailError === 'This field must be unique.') {
        setIsUserExistInWorkspace(true);
        inviteOperation.fail('email-taken');

        return null;
      }

      inviteOperation.fail('request');

      // error on  createUser.mutateAsync
      enqueueSnackbar({
        removable: true,
        variant: 'error',
        title: t('Error'),
        textLayout: 'multiline',
        subTitle: t(
          'En error occurred while adding the user. Please try again',
        ),
      });
    }
  };

  const handleClose = () => {
    setIsNewModalOpened(false);
    setIsUserExistInWorkspace(false);
  };

  const handleEditClose = () => {
    setEditTarget(null);
  };

  const handleEditUser = async (params: HandleSubmitPramsType) => {
    if (editTarget?.id == null) {
      return null;
    }

    if (
      isAgencyCompany &&
      params.clients &&
      !isEqual(params.clients, editTarget.assignedClients as number[]) &&
      editTarget.id
    ) {
      try {
        await assignClients.mutateAsync({
          clients: params.clients,
          user: editTarget.id,
        });
      } catch {
        enqueueSnackbar({
          removable: true,
          variant: 'error',
          title: t('Failure'),
          textLayout: 'multiline',
          subTitle: t('Something went wrong. Please try again'),
        });

        return null;
      }
    }

    try {
      // Same split as create: the Mint 2.0 table talks to the v2 endpoint as JSON.
      const result = isAgGridEnabled
        ? await editUserV2.mutateAsync({
            urlParams: { id: editTarget.id },
            data: params.userData,
          })
        : await editUser.mutateAsync({
            ...params.userData,
            id: editTarget.id,
          });

      setEditTarget(null);

      enqueueSnackbar({
        removable: true,
        variant: 'success',
        title: t('Success'),
        textLayout: 'multiline',
        subTitle: t('User updated.'),
      });

      await refreshUsers();

      return result;
    } catch {
      enqueueSnackbar({
        removable: true,
        variant: 'error',
        title: t('Failure'),
        textLayout: 'multiline',
        subTitle: t('Something went wrong. Please try again'),
      });

      return null;
    }
  };

  return {
    users,
    refetchUsers,
    editTarget,
    setEditTarget,
    isUserExistInWorkspace,
    setIsUserExistInWorkspace,
    userTableV2Ref,
    handleCreate,
    handleClose,
    handleEditClose,
    handleEditUser,
  };
};

export default useUserPageActions;
